import { supabase } from "@foundation/utils/supabaseClient";

export interface PromptTemplate {
  id: string;
  user_id: string;
  name: string;
  description: string;
  content: string;
  variables: string[];
  created_at: string;
  updated_at: string;
}

export interface PromptFormData {
  name: string;
  description: string;
  content: string;
  variables?: string[];
}

// Default prompt template that's always available
const getDefaultPrompt = (): PromptTemplate => ({
  id: "default-proposal-prompt",
  user_id: "system",
  name: "Professional Proposal Generator",
  description:
    "Data-driven AI prompt that generates professional proposals using only provided information",
  content: `You are an expert business proposal writer and project scoping specialist. Your task is to generate a comprehensive, professional proposal using ONLY the information provided in the context. You must NOT create, assume, or hallucinate any information not explicitly provided.

CRITICAL INSTRUCTIONS - ANTI-HALLUCINATION PROTOCOL:

1. USE ONLY PROVIDED DATA: Base all content exclusively on:
   - Client information provided in the context
   - Project details and description from requirements
   - Manual requirements input provided by the user
   - Knowledge base document content (if selected)
   - Template structure and placeholders

2. FORBIDDEN ACTIONS:
   - Do NOT invent specific prices, dates, or numbers not provided
   - Do NOT create fictional team members, company names, or case studies
   - Do NOT assume technical specifications not mentioned in requirements
   - Do NOT generate generic industry statistics without source data
   - Do NOT create placeholder content - use actual provided information

3. REQUIRED ACTIONS:
   - Extract and utilize ALL relevant information from knowledge base documents
   - Reference specific client requirements and constraints mentioned in input
   - Use actual project scope and objectives from the provided requirements
   - Incorporate real client business needs based on provided information
   - Focus on the specific project requirements rather than generic assumptions

DATA UTILIZATION STRATEGY:
- Client Analysis: Use provided client information to personalize every section
- Requirements Integration: Incorporate specific requirements and constraints from manual input
- Knowledge Base Mining: Extract relevant methodologies, case studies, and technical details from selected documents
- Template Adaptation: Fill template placeholders with actual data, not generic content

PROPOSAL GENERATION APPROACH:

1. Information Extraction Phase:
   - Parse all provided client data and requirements
   - Identify key information from knowledge base documents
   - Map requirements to template sections

2. Content Generation Phase:
   - Replace template placeholders with actual, relevant content
   - Ensure every statement is backed by provided information
   - Maintain professional tone while being specific and factual

3. Quality Assurance Phase:
   - Verify all content references provided data
   - Ensure no generic or assumed information is included
   - Confirm client-specific customization throughout

OUTPUT REQUIREMENTS:
- Generate proposal in markdown format with proper structure
- Replace ALL template placeholders with actual content from provided data
- Ensure every section contains specific, relevant information
- Maintain professional language while being factual and data-driven
- Include specific references to client requirements and knowledge base content
- Create compelling content that demonstrates understanding of client's actual needs

AVAILABLE DATA CONTEXT:
The following core data will be available for use in the proposal:
- Client information provided in the context
- Project details and requirements
- Requirements: User-provided requirements text and project description
- Knowledge Base: Selected document content (if any)

Generate a comprehensive, data-driven proposal that builds trust through specificity and demonstrates deep understanding of the client's actual requirements and context. Focus on the specific project needs rather than making assumptions about industry, timeline, or budget that may not be accurate.`,
  variables: [],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

// Fetch all prompts for the current user - Hide default if user has custom version
export const fetchPrompts = async (): Promise<PromptTemplate[]> => {
  try {
    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error("Error getting user:", userError);
      throw userError;
    }

    const userId = userData.user?.id;
    if (!userId) {
      console.error("No user ID found, user may not be authenticated");
      throw new Error("User not authenticated");
    }

    // Fetch prompts for this user
    const { data, error } = await supabase
      .from("scopingai_prompttemplates")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching prompts:", error);
      throw error;
    }

    const userPrompts = data || [];

    // Check if user has a custom version of the default prompt
    const hasCustomVersionOfDefault = userPrompts.some(
      (prompt) =>
        prompt.name === "Professional Proposal Generator" ||
        prompt.description?.includes("custom version of default")
    );

    if (hasCustomVersionOfDefault) {
      // User has customized the default prompt, show only their custom versions
      console.log(
        `✅ User has custom prompt versions, showing ${userPrompts.length} custom prompts only`
      );
      return userPrompts;
    } else {
      // User hasn't customized prompts yet, show default + any other custom prompts
      const defaultPrompt = getDefaultPrompt();
      console.log(
        `✅ Showing default prompt + ${userPrompts.length} custom prompts`
      );
      return [defaultPrompt, ...userPrompts];
    }
  } catch (err) {
    console.error("Error in fetchPrompts:", err);
    throw err;
  }
};

// Save a new prompt
export const savePrompt = async (
  promptData: PromptFormData
): Promise<PromptTemplate> => {
  try {
    if (!promptData.name.trim() || !promptData.content.trim()) {
      throw new Error("Prompt name and content are required");
    }

    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) throw userError;

    const userId = userData.user?.id;
    if (!userId) throw new Error("User not authenticated");

    // Extract variables from the content using {{variableName}} pattern
    const variableMatches = promptData.content.match(/\{\{([^}]+)\}\}/g) || [];
    const variables = variableMatches.map((match: string) =>
      match.substring(2, match.length - 2).trim()
    );

    const { data, error } = await supabase
      .from("scopingai_prompttemplates")
      .insert([
        {
          user_id: userId,
          name: promptData.name,
          description: promptData.description || "",
          content: promptData.content,
          variables: variables,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ])
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Error saving prompt:", err);
    throw err;
  }
};

// Create custom version of default prompt
export const createCustomVersionOfDefaultPrompt = async (customizations: {
  name?: string;
  description?: string;
  content?: string;
}): Promise<PromptTemplate> => {
  try {
    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) throw userError;

    const userId = userData.user?.id;
    if (!userId) throw new Error("User not authenticated");

    const defaultPrompt = getDefaultPrompt();

    // Create custom version with user modifications
    const customPromptData: PromptFormData = {
      name: customizations.name || defaultPrompt.name,
      description:
        customizations.description ||
        `${defaultPrompt.description} (custom version of default)`,
      content: customizations.content || defaultPrompt.content,
    };

    const result = await savePrompt(customPromptData);

    console.log(
      `✅ Created custom version of default prompt for user ${userId}`
    );
    return result;
  } catch (err) {
    console.error("Error creating custom version of default prompt:", err);
    throw err;
  }
};

// Update an existing prompt
export const updatePrompt = async (
  id: string,
  promptData: PromptFormData
): Promise<PromptTemplate> => {
  try {
    if (!promptData.name.trim() || !promptData.content.trim()) {
      throw new Error("Prompt name and content are required");
    }

    // Extract variables from the content using {{variableName}} pattern
    const variableMatches = promptData.content.match(/\{\{([^}]+)\}\}/g) || [];
    const variables = variableMatches.map((match: string) =>
      match.substring(2, match.length - 2).trim()
    );

    const { data, error } = await supabase
      .from("scopingai_prompttemplates")
      .update({
        name: promptData.name,
        description: promptData.description || "",
        content: promptData.content,
        variables: variables,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Error updating prompt:", err);
    throw err;
  }
};

// Delete a prompt
export const deletePrompt = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("scopingai_prompttemplates")
      .delete()
      .eq("id", id);

    if (error) throw error;
  } catch (err) {
    console.error("Error deleting prompt:", err);
    throw err;
  }
};

// Get prompt by ID
export const getPromptById = async (
  id: string
): Promise<PromptTemplate | null> => {
  try {
    const { data, error } = await supabase
      .from("scopingai_prompttemplates")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // Row not found
        return null;
      }
      throw error;
    }

    return data;
  } catch (err) {
    console.error("Error fetching prompt by ID:", err);
    throw err;
  }
};

// Search prompts
export const searchPrompts = async (
  query: string
): Promise<PromptTemplate[]> => {
  try {
    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) throw userError;

    const userId = userData.user?.id;
    if (!userId) throw new Error("User not authenticated");

    const { data, error } = await supabase
      .from("scopingai_prompttemplates")
      .select("*")
      .eq("user_id", userId)
      .or(
        `name.ilike.%${query}%,description.ilike.%${query}%,content.ilike.%${query}%`
      )
      .order("created_at", { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (err) {
    console.error("Error searching prompts:", err);
    throw err;
  }
};
